import {themeEl} from '@ui/root-el';
import {useSettings} from '@ui/settings/use-settings';
import {CssTheme} from '@ui/themes/css-theme';
import {usePreferredColorScheme} from '@ui/themes/use-preferred-color-scheme';
import {getCurrentThemeValue} from '@ui/themes/utils/get-current-theme-value';
import {useCookie} from '@ui/utils/hooks/use-cookie';
import {getBootstrapData} from '@ui/bootstrap-data/bootstrap-data-store';
import {createContext, useCallback, useEffect, useMemo, useState} from 'react';

type ColorScheme = 'dark' | 'light' | 'system';

export interface ColorSchemeContextValue {
  colorScheme: Omit<ColorScheme, 'system'>;
  setColorScheme: (colorScheme: ColorScheme) => void;
  selectedTheme: CssTheme | null;
  selectableThemes: CssTheme[];
  setSelectedTheme: (theme: CssTheme) => void;
}

export const ColorSchemeContext = createContext<ColorSchemeContextValue>(null!);

export function ColorSchemeProvider({
  children,
  cookieName,
  themeCookieName = 'be-css-theme',
}: {
  children: any;
  cookieName?: string;
  themeCookieName?: string;
}) {
  const {themes} = useSettings();
  const selectableThemes = getBootstrapData().css_themes ?? [];
  const [appliedScheme, setAppliedScheme] = useState<
    Omit<ColorScheme, 'system'>
  >(() =>
    document.documentElement.classList.contains('dark') ? 'dark' : 'light',
  );
  const initialTheme = getThemeFromDom(selectableThemes);
  const [selectedTheme, setSelectedThemeState] = useState<CssTheme | null>(
    initialTheme,
  );
  const adminSelectedDefaultScheme: ColorScheme =
    themes?.default_scheme ?? 'system';
  const userPreferredColorScheme = usePreferredColorScheme();
  const defaultScheme: Omit<ColorScheme, 'system'> =
    adminSelectedDefaultScheme === 'system'
      ? userPreferredColorScheme
      : adminSelectedDefaultScheme;
  const [schemeCookie, setCurrentSchemeCookie] = useCookie(
    cookieName ?? 'be-color-scheme',
  );
  const [themeCookie, setThemeCookie] = useCookie(themeCookieName);
  const selectedScheme =
    schemeCookie === 'light' || schemeCookie === 'dark'
      ? schemeCookie
      : defaultScheme;

  const setColorScheme = useCallback(
    (scheme: ColorScheme) => {
      setCurrentSchemeCookie(scheme);
      setThemeCookie('', {days: -1});
      const colorScheme =
        scheme === 'light' || scheme === 'dark'
          ? scheme
          : userPreferredColorScheme;
      const theme = getDefaultThemeForScheme(selectableThemes, colorScheme);
      setSelectedThemeState(theme);
      setAppliedScheme(colorScheme);
      applyColorSchemeToDom(colorScheme, theme);
    },
    [selectableThemes, setCurrentSchemeCookie, setThemeCookie, userPreferredColorScheme],
  );

  const setSelectedTheme = useCallback(
    (theme: CssTheme) => {
      const scheme = theme.is_dark ? 'dark' : 'light';
      setCurrentSchemeCookie(scheme);
      setThemeCookie(`${theme.id}`);
      setSelectedThemeState(theme);
      setAppliedScheme(scheme);
      applyColorSchemeToDom(scheme, theme);
    },
    [setCurrentSchemeCookie, setThemeCookie],
  );

  // if selected theme is different then the one that was set
  // with server render, set new css variables, this will only
  // happen if user has not selected theme manually and default theme is set to "system"
  useEffect(() => {
    if (appliedScheme !== selectedScheme && !themeCookie) {
      const theme = getDefaultThemeForScheme(selectableThemes, selectedScheme);
      setAppliedScheme(selectedScheme);
      setSelectedThemeState(theme);
      applyColorSchemeToDom(selectedScheme, theme);
    }
  }, [selectedScheme, appliedScheme, themeCookie, selectableThemes]);

  const contextValue: ColorSchemeContextValue = useMemo(() => {
    return {
      colorScheme: appliedScheme,
      setColorScheme,
      selectedTheme,
      selectableThemes,
      setSelectedTheme,
    };
  }, [appliedScheme, setColorScheme, selectedTheme, selectableThemes, setSelectedTheme]);

  return (
    <ColorSchemeContext.Provider value={contextValue}>
      {children}
    </ColorSchemeContext.Provider>
  );
}

export function applyColorSchemeToDom(
  scheme: Omit<ColorScheme, 'system'>,
  theme?: CssTheme | null,
) {
  if (scheme === 'dark') {
    themeEl.classList.add('dark');
    themeEl.classList.remove('light');
  } else {
    themeEl.classList.remove('dark');
    themeEl.classList.add('light');
  }

  if (theme) {
    removeThemeClasses();
    themeEl.classList.add(getThemeClassName(theme));
  }

  const themeColorMetaEl = document.querySelector('meta[name="theme-color"]');
  if (themeColorMetaEl) {
    themeColorMetaEl.setAttribute(
      'content',
      getCurrentThemeValue('--be-background'),
    );
  }
}

function getThemeFromDom(themes: CssTheme[]): CssTheme | null {
  return (
    themes.find(theme =>
      document.documentElement.classList.contains(getThemeClassName(theme)),
    ) ?? null
  );
}

function getDefaultThemeForScheme(
  themes: CssTheme[],
  scheme: Omit<ColorScheme, 'system'>,
): CssTheme | null {
  return (
    themes.find(theme =>
      scheme === 'dark' ? theme.default_dark : theme.default_light,
    ) ?? themes.find(theme => !!theme.is_dark === (scheme === 'dark')) ?? null
  );
}

function removeThemeClasses() {
  [...themeEl.classList]
    .filter(className => className.startsWith('be-theme-'))
    .forEach(className => themeEl.classList.remove(className));
}

function getThemeClassName(theme: CssTheme): string {
  return `be-theme-${theme.id}`;
}
